// SAB-642: cold-start chooser. Landing-chain rule 4 (a signed-in user with no NFL
// membership and no in-season MLB team) lands here instead of the create-only wizard, so
// joining — the majority path for our invite-based growth — is reachable without backing
// out of create. "Never force either": create is one option among a real choice.
//
// Order: Join with a code (primary), Browse public leagues (conditional), Create.
//
// Sport-agnostic code entry: an invitee may not know whether their code is NFL or MLB. We
// resolve the code across BOTH sports HERE and route to the correct join screen, so an MLB
// code never hits the NFL "not found" path. This is a NEW additive resolver at a new entry
// point; it does NOT touch the per-sport join pages' lookup/error copy (that is SAB-619).
//
// Browse is shown ONLY when public leagues are actually browseable (VITE_PUBLIC_LEAGUES_ENABLED
// on AND at least one public joinable league). Today the flag is off and the count is 0, so
// Browse is hidden — a cold user hitting an empty browse is worse than not offering it. It
// reappears automatically once the flag flips and leagues exist.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Ticket, Compass, PlusCircle, Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import sabotagelogo from '@/assets/sabotage-logo.png';

const PUBLIC_LEAGUES_ENABLED = import.meta.env.VITE_PUBLIC_LEAGUES_ENABLED === 'true';

function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export default function NFLStartChooser() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Browse is hidden until we know there is at least one public joinable league.
  const [showBrowse, setShowBrowse] = useState(false);

  useEffect(() => {
    // Only query when the feature is on; when off, Browse stays hidden with no request.
    if (!PUBLIC_LEAGUES_ENABLED) return;
    let cancelled = false;
    (async () => {
      const { count, error: qErr } = await (supabase as any)
        // SAB security H3: safe public-browse view (no direct nfl_leagues read).
        .from('nfl_public_leagues')
        .select('id', { count: 'exact', head: true })
        .in('status', ['setup', 'pre_draft']);
      if (!cancelled && !qErr && (count ?? 0) > 0) setShowBrowse(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const onJoinWithCode = async () => {
    const normalized = normalizeCode(code);
    setError(null);
    if (normalized.length < 4) { setError('Enter the invite code your commissioner shared.'); return; }
    setResolving(true);
    try {
      // NFL first — same public lookup the NFL join page uses.
      const nflRes = await fetch(
        `${(supabase as any).supabaseUrl}/functions/v1/nfl-join-league?invite_code=${normalized}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      const nflData = await nflRes.json().catch(() => ({}));
      if (nflRes.ok && !nflData?.error) { navigate(`/nfl/join/${normalized}`); return; }

      // Then MLB — the beta code lookup.
      const { data: mlb } = await supabase.rpc('find_league_by_invite_code', { code: normalized });
      const mlbLeague = Array.isArray(mlb) ? mlb[0] : mlb;
      if (mlbLeague) { navigate(`/beta/join/${normalized}`); return; }

      // Genuinely in neither sport.
      setError('No league found with that code. Check it with whoever invited you.');
    } catch {
      setError('We could not look up that code. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-[440px]">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-11 w-auto mx-auto mb-8" />
        <h1 className="text-center font-display font-bold text-[22px] text-foreground">You're in. What next?</h1>
        <p className="mt-2 text-center text-[13.5px] text-muted-foreground leading-[1.5]">
          Join a friend's league with a code, or start your own.
        </p>

        {/* 1 — Join with a code (primary path) */}
        <div className="mt-7 rounded-2xl p-4 border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.14)' }}>
          <div className="flex items-center gap-2.5">
            <Ticket className="h-5 w-5 text-[hsl(var(--primary))] shrink-0" />
            <span className="font-display font-semibold text-[15px] text-foreground">Join with a code</span>
          </div>
          <p className="mt-1 text-[12.5px] text-muted-foreground leading-[1.5]">Have an invite code? Enter it — we'll find the league.</p>
          <div className="mt-3 flex gap-2">
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value); if (error) setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') void onJoinWithCode(); }}
              placeholder="Invite code"
              autoCapitalize="characters"
              className="flex-1 h-12 rounded-xl bg-background border border-border px-3.5 text-foreground text-[15px] tracking-wide outline-none focus:border-[hsl(var(--primary))]"
            />
            <button
              type="button"
              onClick={() => void onJoinWithCode()}
              disabled={resolving}
              className="h-12 px-4 rounded-xl font-display font-bold text-[15px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join'}
            </button>
          </div>
          {error && (
            <p className="mt-2 flex items-start gap-1.5 text-[12.5px] text-red-400 leading-[1.45]">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{error}
            </p>
          )}
        </div>

        {/* 2 — Browse public leagues (only when there are any) */}
        {showBrowse && (
          <button
            type="button"
            onClick={() => navigate('/nfl/leagues/browse')}
            className="mt-3 w-full rounded-2xl p-4 border text-left flex items-center gap-3 hover:border-[hsl(var(--primary)/0.4)] transition-colors"
            style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.12)' }}
          >
            <Compass className="h-5 w-5 text-foreground shrink-0" />
            <span className="flex-1">
              <span className="block font-display font-semibold text-[15px] text-foreground">Browse public leagues</span>
              <span className="block text-[12.5px] text-muted-foreground">Find an open league to join.</span>
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
          </button>
        )}

        {/* 3 — Create a league */}
        <button
          type="button"
          onClick={() => navigate('/nfl/create')}
          className="mt-3 w-full rounded-2xl p-4 border text-left flex items-center gap-3 hover:border-[hsl(var(--primary)/0.4)] transition-colors"
          style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.12)' }}
        >
          <PlusCircle className="h-5 w-5 text-foreground shrink-0" />
          <span className="flex-1">
            <span className="block font-display font-semibold text-[15px] text-foreground">Create a league</span>
            <span className="block text-[12.5px] text-muted-foreground">Be the commissioner and invite your friends.</span>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
        </button>
      </div>
    </div>
  );
}
